import { useMutation } from "@tanstack/react-query";
import {
  EditOutline24Icon,
  InformationOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { Preview } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/Preview";
import { PreviewResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import { clamp } from "features/CenterContent/shared/utils";
import { TrainResumed } from "features/Header/TrainResumed/useTrainResumed";
import { getInfoTitleFromParams } from "features/VehicleSidebar/AffectedTrains/utils/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Checkbox,
  DropsBadge,
  Flex,
  Input,
  Separator,
  Text,
} from "shared/components/Spor";
import { TextTooltip } from "shared/components/TextTooltip";
import styled, { useTheme } from "styled-components";

const NoLabelInput = styled(Input)`
  &&& {
    padding-top: 0px;
  }
`;

export const TrainResumedElement = ({
  trainResumed,
  trainResumedIsFinished,
}: {
  trainResumed: TrainResumed;
  trainResumedIsFinished: () => void;
}) => {
  const { t } = useTranslation();
  const {
    eventType,
    trainIdentifier,
    affectedStopName,
    originStopName,
    destinationStopName,
    actualTime,
    scheduledTime,
    trainInformationUuid,
    trainInformationSummary,
    preview,
  } = trainResumed;
  const minutesLate = () => {
    const actualTimeDate = new Date(actualTime);
    const scheduledTimeDate = new Date(scheduledTime);
    const differenceInMinutes = Math.floor(
      (actualTimeDate.getTime() - scheduledTimeDate.getTime()) / (1000 * 60),
    );
    return differenceInMinutes;
  };

  const MIN_VALUE = 1;
  const MAX_VALUE = 60 * 24;
  const minutesLateClamped = () => clamp(MIN_VALUE, MAX_VALUE, minutesLate());

  const theme = useTheme();
  const [askConfirmation, setAskConfirmation] = useState(false);
  const [registerDelay, setRegisterDelay] = useState(false);
  const [editDelay, setEditDelay] = useState(false);
  const [delay, setDelay] = useState(minutesLateClamped());
  const [inputDelay, setInputDelay] = useState<number | null>(null);

  const isTrainStopped = eventType === TrainEventTypeEnum.TRAIN_STOPPED;
  const delayPath = isTrainStopped
    ? "close-and-register-delay"
    : "change-to-delay";
  const buttonText = isTrainStopped ? "Friskmeld" : "Behandle";
  const closePath = registerDelay ? delayPath : "close";
  const description =
    !isTrainStopped && registerDelay
      ? "Er du sikker på at du vil endre hendelsen?"
      : "Er du sikker på at du vil friskmelde hendelsen?";
  const {
    mutateAsync: closeTrainStatus,
    isError: isErrorClose,
    isSuccess: isSuccessClose,
  } = useMutation({
    mutationKey: ["closeTrainStatus", trainInformationUuid],
    mutationFn: () =>
      mutationFnPOST<string, { delay?: number }>(
        `${getBackendUrl()}/trainInformation/information/${trainInformationUuid}/${closePath}`,
        { delay },
      ),
  });

  const {
    mutate: removeTrain,
    isError: isErrorRemove,
    isSuccess: isSuccessRemove,
  } = useMutation({
    mutationKey: ["removeTrain", trainInformationUuid],
    mutationFn: () =>
      mutationFnPOST<string, undefined>(
        `${getBackendUrl()}/train-resumed/remove-train/${trainResumed.trainInformationUuid}`,
      ),
  });

  const {
    mutate: ignoreTrain,
    isError: isErrorIgnore,
    isSuccess: isSuccessIgnore,
  } = useMutation({
    mutationKey: ["ignoreTrain", trainInformationUuid],
    mutationFn: () =>
      mutationFnPOST<string, undefined>(
        `${getBackendUrl()}/train-resumed/ignore-train/${trainResumed.trainInformationUuid}`,
      ),
  });

  const [showSpinner, setShowSpinner] = useState(false);

  const isErrorCloseRemove = isErrorClose || isErrorRemove;
  const isSuccessCloseRemove = isSuccessClose && isSuccessRemove; // Both must be successful

  const isError = isErrorCloseRemove || isErrorIgnore;
  const isSuccess = isSuccessCloseRemove || isSuccessIgnore;

  const eventTypeName = getInfoTitleFromParams(
    t,
    eventType,
    affectedStopName,
    null,
  );

  useEffect(() => {
    if (!isSuccess) return;
    setTimeout(() => {
      setShowSpinner(false);
      setAskConfirmation(false);
      trainResumedIsFinished();
    }, 1000);
  }, [isSuccess]);

  useEffect(() => {
    if (!isError) return;
    setShowSpinner(false);
  }, [isError]);

  const onChangeCheckbox = () => {
    setRegisterDelay(!registerDelay);
    setEditDelay(false);
  };

  // We make the preview using delay=1 in the backend, and replace
  // the delay in frontend to prevent further unnecessary api calls
  const updatePreviewText = (text: string | null) =>
    text?.replace("1 min", `${delay} min`) ?? null;
  const previewData: PreviewResponse = {
    internalContext: preview?.internalContext,
    internalTitle: updatePreviewText(preview?.internalTitle),
    internalMessage: updatePreviewText(preview?.internalMessage),
    externalContext: preview?.externalContext,
    externalTitle: updatePreviewText(preview?.externalTitle),
    externalMessage: updatePreviewText(preview?.externalMessage),
  };

  return (
    <Flex
      flexDir="column"
      p="12px"
      gap="12px"
      borderRadius="xs"
      backgroundColor={theme.colorBackgroundSecondary}
    >
      <Flex flexDir="column" py="6px" gap="6px">
        <Flex gap="12px">
          <Flex flexDir="column" gap="3px" width={0}>
            <Flex gap="9px">
              <Text whiteSpace="pre" variant="sm">
                <b>Tog kjører igjen</b>
              </Text>
              <Text
                variant="sm"
                whiteSpace="pre"
                style={{ color: theme.colorTextSecondary }}
              >
                {minutesLate()} min forsinket
              </Text>
              <TextTooltip showArrow label={trainInformationSummary}>
                <InformationOutline24Icon minWidth="24px" minHeight="24px" />
              </TextTooltip>
            </Flex>
            <Text
              variant="sm"
              style={{
                width: "275px",
                // Behaviour: Use max 3 lines and show "..." at overflow
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: "3",
                WebkitBoxOrient: "vertical",
              }}
            >
              {trainIdentifier.identifier} {originStopName} -{" "}
              {destinationStopName}
            </Text>
          </Flex>
        </Flex>
        <Flex>
          <DropsBadge semantics="warning">{eventTypeName}</DropsBadge>
        </Flex>
        <Flex gap="6px" alignItems="center" justifyContent="flex-end">
          {!askConfirmation && (
            <>
              <TextTooltip
                label="Fjerner meldingen fra lista. Ny melding kommer ved evt neste passeringsvarsel."
                showArrow
              >
                <Button
                  variant="tertiary"
                  disabled={showSpinner}
                  onClick={() => {
                    setShowSpinner(true);
                    ignoreTrain();
                  }}
                  alignSelf="end"
                  mr="3px"
                  size="sm"
                >
                  <Text variant="sm">Ignorer</Text>
                </Button>
              </TextTooltip>

              <Button
                variant="primary"
                onClick={() => setAskConfirmation(true)}
                alignSelf="end"
                mr="4px"
                disabled={!trainInformationUuid}
                size="sm"
              >
                <Text variant="sm">{buttonText}</Text>
              </Button>
            </>
          )}
        </Flex>
      </Flex>
      {askConfirmation && (
        <>
          <Separator borderBottomWidth="1px" />
          <Text mt="6px">{description}</Text>
          <Flex gap="12px" minHeight="36px" alignItems="center">
            <Checkbox
              checked={registerDelay}
              onCheckedChange={onChangeCheckbox}
            />
            <Text onClick={onChangeCheckbox}>
              Registrer forsinkelse
              {registerDelay ? ` (${delay} min)` : ``}
            </Text>
            {registerDelay && !editDelay && (
              <Button
                size="xs"
                variant="ghost"
                leftIcon={<EditOutline24Icon />}
                onClick={() => {
                  setEditDelay(true);
                }}
              >
                Endre
              </Button>
            )}
          </Flex>
          {registerDelay && !editDelay && (
            <Preview previewStatus="success" previewData={previewData} />
          )}
          {!editDelay && (
            <Flex gap="12px" alignSelf="end">
              <Button
                size="sm"
                variant="tertiary"
                onClick={() => {
                  setAskConfirmation(false);
                  setRegisterDelay(false);
                  setDelay(minutesLateClamped());
                }}
              >
                <Text variant="sm">Avbryt</Text>
              </Button>
              <Button
                size="sm"
                loading={showSpinner}
                onClick={() => {
                  setShowSpinner(true);
                  void closeTrainStatus().then(() => removeTrain());
                }}
                variant="primary"
              >
                <Text variant="sm" fontWeight={700}>
                  Ja
                </Text>
              </Button>
            </Flex>
          )}
        </>
      )}
      {editDelay && (
        <>
          <Separator />
          <Flex flexDir="column" gap="6px" mt="6px">
            <Text>Endre antall minutter forsinkelse</Text>
            <NoLabelInput
              type="number"
              label=""
              placeholder={`${delay} (min)`}
              width="30%"
              _placeholder={{ opacity: 0.75, color: theme.colorTextMain }}
              onChange={(e) => {
                if (e.target.value !== "") {
                  setInputDelay(
                    clamp(MIN_VALUE, MAX_VALUE, parseInt(e.target.value, 10)),
                  );
                } else {
                  setInputDelay(null);
                }
              }}
            />
            <Flex alignSelf="end">
              <Button
                onClick={() => {
                  if (inputDelay !== null) {
                    setDelay(inputDelay);
                  }
                  setInputDelay(null);
                  setEditDelay(false);
                }}
                variant="primary"
              >
                Lagre
              </Button>
            </Flex>
          </Flex>
        </>
      )}
      {isError && (
        <Alert data-color="critical">Noe gikk galt, vennligst prøv igjen</Alert>
      )}
      {isSuccessClose && <Alert data-color="success">Hendelse opprettet</Alert>}
    </Flex>
  );
};
